import { PaymentMethodApi } from '@app/services/apiServices/PaymentMethodApi';
import { PaymentMethod } from '@app/models/PaymentMethod';
import { UtilService } from '@app/services/Util.service';
import { SpinnerService } from '@app/services/spinner.service';
import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { FTNIApi, FTNIAchTokenResponse } from '../../../services/apiServices/FTNIApi';
import { CheckingAccount } from '@app/models/CheckingAccount';
import { Homeowner } from '@app/models/Homeowner';
import { UserStore } from '@app/services/stores/UserStore';
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { DetailsPropagateDefaultDialog } from "@app/pages/wallet/dialogs/details-propagate-default.dialog";
import { CheckRoutingNumberHelperDialog } from "@app/pages/wallet/checkadd/checkRoutingNumberHelper.dialog";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'app-checking-account-details',
    template: require('./checkingAccountDetails.html')
})
export class CheckingAccountDetailsComponent implements OnInit {
    paymentMethod: PaymentMethod;
    isValidRoutingNumber: boolean;
    routingErrorMsg: string;
    doneLoading: boolean;
    defaultMethodBoolean: boolean;
    previousNickname: string;
    // TODO change to correct object list once api is done
    paymentSchedules: any[];
    checkingAccount: CheckingAccount;
    tokenResponse: FTNIAchTokenResponse;
    savingsAccountIndicator: string;
    homeowner: Homeowner;
    private defaultMethodPaymentSchedules: PaymentSchedule[];

    constructor(@Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(UtilService) private utilService: UtilService,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(FTNIApi) private ftniApi: FTNIApi,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {}

    ngOnInit() {

        this.doneLoading = false;
        this.spinnerService.showSpinner();
        this.get().then(() => {
            this.previousNickname = this.paymentMethod.nickname;
            this.spinnerService.closeSpinner();
            this.doneLoading = true;
        }, () => {
            this.spinnerService.closeSpinner();
            this.dialog.open(DialogErrorDialog, {
                data: { error: 'Variable Error message based on context/code. ' }
            });
        });
        if (!this.defaultMethodBoolean)
            this.paymentMethodApi.getDefaultPaymentMethodSchedules(this.userStore.getHoaHomeownerId())
                .pipe(take(1)).subscribe({
                    next: resp => {
                        this.defaultMethodPaymentSchedules = resp;
                    } });
    }

    // method for viewing a checking account detail
    get(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const paymentMethodId: string = this.route.snapshot.params.paymentMethodId || '0';

            this.paymentMethodApi.paymentMethodsIdGet(paymentMethodId)
                .pipe(take(1)).subscribe({
                    next: resp => {
                        this.paymentMethod = resp;
                        this.savingsAccountIndicator = this.paymentMethod.bankAccountType;
                        this.setCheckingAccountFromPaymentMethod();
                        this.defaultMethodBoolean = Object.assign({}, this.paymentMethod.defaultMethod);
                        this.checkRoutingNumber();
                        this.paymentSchedules = [{
                            scheduleNickname: 'First Real Schedule',
                            endDate: { formattedFullDate: '12/11/2017' },
                            totalPaymentAmount: '123.45'
                        }];
                        resolve();
                    },
                    error: err => {
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.get();
                                }
                                reject();
                            });
                    }
                });
        })
    }

    invalid() {
        return !(this.isValidRoutingNumber && this.paymentMethod.nickname);
    }

    preSave() {
        if (this.paymentMethod.defaultMethod &&
            this.defaultMethodPaymentSchedules &&
            this.defaultMethodPaymentSchedules.length > 0) {
            const dialogRef = this.dialog.open(DetailsPropagateDefaultDialog, {
                data: { paymentSchedules: this.defaultMethodPaymentSchedules, type: 'Save' }
            });
            dialogRef.afterClosed().pipe(take(1)).subscribe((data) => {
                if (data === 'Save') {
                    this.save(false);
                } else if (data === 'Update') {
                    this.save(true);
                }
            });
        } else {
            this.save(false);
        }
    }

    // method for saving an existing checking account
    save(propagatePmtMethod: boolean) {
        this.spinnerService.showSpinner();

        let existingToken = this.paymentMethod.token;
        this.setCheckingAccountFromPaymentMethod();
        let existingCheckingAccount = this.checkingAccount;

        this.paymentMethodApi.paymentMethodsIdAllowedUpdateGet('' + this.paymentMethod.id)
            .pipe(take(1)).subscribe({
                next: () => {

                    this.ftniApi.privateProcessACHPostUpdateToken(existingCheckingAccount, existingToken)
                        .pipe(take(1)).subscribe({
                            next: resp => {
                                this.tokenResponse = resp;

                                if (this.tokenResponse.BankToken) {

                                    this.paymentMethod.bankAccountType = this.savingsAccountIndicator;
                                    this.paymentMethod.token = resp.BankToken;

                                    this.paymentMethodApi.paymentMethodsPost(this.paymentMethod)
                                        .pipe(take(1)).subscribe({
                                            next:
                                    async (resp) => {
                                        if (propagatePmtMethod &&
                                            this.defaultMethodPaymentSchedules &&
                                            this.defaultMethodPaymentSchedules.length) {
                                            this.paymentMethodApi.paymentMethodPropagateToSchedules(this.defaultMethodPaymentSchedules, resp,
                                                this.defaultMethodPaymentSchedules[0].paymentMethod.type !== 'ach')
                                                .then(resp => {
                                                    if (resp && resp.length > 0) {
                                                        this.dialog.open(DialogErrorDialog, {
                                                            data: {
                                                                message: 'An error occurred when attempting to save or update Payment Schedules.' +
                                                                        ' Please verify that the schedules: ' + resp.join(',') +
                                                                        ' were processed correctly. If not, you may need to change the payment' +
                                                                        ' method manually.'
                                                            }
                                                        });
                                                    }
                                                },
                                                () => {
                                                    this.dialog.open(DialogErrorDialog, {
                                                        data: {
                                                            message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                                                    'Please verify that the schedules were processed correctly. ' +
                                                                    'If not, you may need to change the payment method manually.'
                                                        }
                                                    });
                                                });
                                        }

                                        this.spinnerService.closeSpinner();
                                        this.router.navigateByUrl('/wallet');
                                    },
                                            error: () => {
                                                // error when trying to update payment method in onesource
                                                let errMsg = 'Your payment method was not able to be updated. Please contact support for more information.';
                                                this.networkErrorService.handleNetworkError(errMsg, true)
                                                    .pipe(take(1)).subscribe((err) => {
                                                        console.error(err);
                                                    });
                                            }
                                        });
                                } else {
                                    // when FTNI response token doesn't match expected token
                                    let errMsg = 'Error updating checking account. Please contact support for more information.';
                                    this.networkErrorService.handleNetworkError(errMsg, true)
                                        .pipe(take(1)).subscribe(() => {
                                            return null;
                                        });
                                }

                            }, error: err => {
                                let errMsg: string = undefined;
                                if (err.error && err.error.message) {
                                    errMsg = err.error.message;
                                }
                                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                                    .pipe(take(1)).subscribe();
                            }
                        });
                }, error: err => {
                    this.spinnerService.closeSpinner();
                    let errMsg: string = undefined;
                    if (err.error && err.error.message) {
                        errMsg = err.error.message;
                    }
                    this.errorHandlerService.handleNetworkError(err, true, errMsg)
                        .pipe(take(1)).subscribe();
                }
            });
    }

    cancel() {
        this.router.navigateByUrl('/wallet/viewcheckdetails/' + this.paymentMethod.id,
            { state: { paymentMethodId: this.paymentMethod.id } });
    }

    // method to check for validRoutingNumber if it is changed
    checkRoutingNumber() {
        if (this.paymentMethod.routingNumber && this.paymentMethod.routingNumber.length === 9) {
            //call api that takes routing number and returns bank name
            this.paymentMethodApi.paymentMethodsRoutingNumberGet(this.paymentMethod.routingNumber, false)
                .pipe(take(1)).subscribe({
                    next: resp => {
                        this.isValidRoutingNumber = resp.isValidRoutingNumber;
                        if (this.isValidRoutingNumber) {
                            this.paymentMethod.bankName = resp.bankName;
                            this.routingErrorMsg = null;
                        } else {
                            this.paymentMethod.bankName = null;
                            this.routingErrorMsg = 'Invalid Routing Number';
                        }
                    },
                    error: err => {
                        this.paymentMethod.bankName = null;
                        this.isValidRoutingNumber = false;
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.checkRoutingNumber();
                                }
                            });
                    }
                });

        } else {
            this.routingErrorMsg = 'Invalid Routing Number';
            this.paymentMethod.bankName = null;
            this.isValidRoutingNumber = false;
        }
    }

    routingNumberHelper() {
        this.dialog.open(CheckRoutingNumberHelperDialog);
    }

    setCheckingAccountFromPaymentMethod() {
        this.checkingAccount = {
            routingNumber: this.paymentMethod.routingNumber,
            accountNumber: '',
            accountNumberConfirm: '',
            payerId: this.paymentMethod.payerId,
            defaultPaymentMethod: this.paymentMethod.defaultMethod,
            accountHolderName: this.userStore.getFirstName() + ' ' + this.userStore.getLastName(),
            bankName: this.paymentMethod.bankName,
            nickname: this.paymentMethod.nickname,
            savingsAccountIndicator: this.savingsAccountIndicator
        };
    }
}

